import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './EvaluationFormView.css'
import axios from 'axios';
import Swal from 'sweetalert2';

import FooterLanding from '../../components/footerLanding/footerLanding';
import StepCompetencesEvaluation from '../../components/stepCompetencesEvaluation/stepCompetencesEvaluation';
import { apiUrl } from '../../utility/Connections';
import { Layout, Progress, message, Modal } from 'antd';
import HeaderEvaluation from '../../components/headerEvaluation/headerEvaluation';
import LoadingOverlay from '../../components/loadingOverlay/loadingOverlay';

const { Content } = Layout;

const resolveInitialStep = (evaluationData) => {
    const questionBlocks = evaluationData?.evr_questions || [];
    const hasAnsweredQuestions = questionBlocks.some((block) =>
        (block?.questions || []).some((question) => {
            const hasAnswer = question?.ean_answer !== null && question?.ean_answer !== undefined && question?.ean_answer !== '';
            const hasText = question?.ean_answer_text !== null && question?.ean_answer_text !== undefined && question?.ean_answer_text !== '';
            return hasAnswer || hasText;
        })
    );

    const hasStartedEvaluation = Boolean(evaluationData?.evr_started || evaluationData?.evr_saved);

    if (!hasStartedEvaluation && !hasAnsweredQuestions) {
        return 0;
    }

    for (let blockIndex = 0; blockIndex < questionBlocks.length; blockIndex += 1) {
        const block = questionBlocks[blockIndex];
        const hasPendingQuestion = (block?.questions || []).some((question) => {
            const hasAnswer = question?.ean_answer !== null && question?.ean_answer !== undefined && question?.ean_answer !== '';
            const hasText = question?.ean_answer_text !== null && question?.ean_answer_text !== undefined && question?.ean_answer_text !== '';
            return !hasAnswer && !hasText;
        });

        if (hasPendingQuestion) {
            return blockIndex + 1;
        }
    }

    // Todos los bloques contestados: llevar al paso final antes de finalizar.
    return questionBlocks.length + 1;
};

const EvaluationFormView = () => {
    const [showLoading, setShowLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [isSavingEvaluation, setIsSavingEvaluation] = useState(false);
    const [dataEvaluationAnswerGroups, setDataEvaluationAnswerGroups] = useState([])
    const [evaluationData, setEvaluationData] = useState(null);
    const [orgLogo, setOrgLogo] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);

    // Policy footer info
    const [policyVersion, setPolicyVersion] = useState(null);
    const [policyContent, setPolicyContent] = useState(null);
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [evaluationTycMeta, setEvaluationTycMeta] = useState(null);

    const { uuid } = useParams();

    useEffect(() => {
        getEvaluationByUUID(true)
    }, [uuid]);

    const fetchPolicyInfo = () => {
        // Fetch policy version + content for the footer note
        return axios.get(`${apiUrl}/terms-conditions/get-for-evaluation/${uuid}/`)
            .then((response) => {
                const { code, data } = response.data;
                if (code === 'SUCCESS' && data) {
                    setPolicyVersion(data.tcc_version);
                    setPolicyContent(data.tcc_content);
                    setEvaluationTycMeta({
                        tcc_id: data.tcc_id,
                        tcc_text_hash: data.tcc_text_hash,
                        requires_acceptance: Boolean(data.requires_acceptance),
                        already_accepted: Boolean(data.already_accepted),
                    });
                }
            })
            .catch(() => { /* silent — footer is informational */ });
    };

    const registerEvaluationTermsAcceptance = async () => {
        const meta = evaluationTycMeta;
        if (!meta || !meta.requires_acceptance || meta.already_accepted) return;
        if (!meta.tcc_id || !meta.tcc_text_hash) return;

        await axios.post(`${apiUrl}/terms-conditions/accept/`, {
            evr_uuid: uuid,
            tcc_id: meta.tcc_id,
            tcc_text_hash: meta.tcc_text_hash,
            user_agent: navigator.userAgent,
            decision: 'ACEPTO',
        });

        setEvaluationTycMeta((prev) => ({
            ...prev,
            requires_acceptance: false,
            already_accepted: true,
        }));
    };

    const getEvaluationByUUID =  (first) => {
        setLoadingText("Cargando información, espera un momento por favor...")
        setShowLoading(true);
        // Lógica para cargar información con el uuid
        axios.get(`${apiUrl}/evaluation-relationships/get-evaluation-relationship-by-uuid/${uuid}/`)
            .then(function (response) {
                const code = response.data.code;
                if (code == "SUCCESS") {
                    const data = response.data.data;
                    setDataEvaluationAnswerGroups(data.evaluation_answer_groups || []);
                    if (first) {
                        setEvaluationData(data);
                        setCurrentStep(resolveInitialStep(data));

                        Promise.allSettled([
                            getOrganizationLogo(data.org_id),
                            fetchPolicyInfo(),
                        ]).finally(() => {
                            setShowLoading(false);
                        });
                    } else {
                        setEvaluationData(data);
                        setShowLoading(false);
                    }
                } else if (code == "EVALUATION_ALREADY_FINISHED") {
                    setEvaluationData(null)
                    setShowLoading(false);
                    Swal.fire({
                        title: 'Atención',
                        text: "Esta evaluación ya se encuentra finalizada",
                        icon: 'Success',
                        confirmButtonColor: '#012E40',
                        cancelButtonColor: '#d33',
                        confirmButtonText: 'Aceptar'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            const rootUrl = window.location.origin;
                            const link = `${rootUrl}/evaluaciones/listado/${response.data.evl_id}/${response.data.email}`;
                            window.location.href = link;
                        }
                    });
                } 
            })
            .catch(function (error) {
                console.error('There was an error!', error);
                setIsSavingEvaluation(false);
                setShowLoading(false);
            });
    }

    const getOrganizationLogo = (orgId) => {
        return axios.get(`${apiUrl}/organizations/get-organization-logo/${orgId}/`)
            .then(function (response) {
                const data = response.data.org_logo;
                setOrgLogo(data);
            })
            .catch(function (error) {
                console.error('There was an error!', error);
            });
    }

    const handleAnswerChange = (dimension, ean_dsc_id, newAnswer) => {
        setEvaluationData(prev => {
            const updatedQuestions = prev.evr_questions.map(block => {
                if (block.dimension !== dimension) return block; // ignorar bloques que no coincidan

                const updatedBlockQuestions = block.questions.map(q => {
                    if (q.ean_dsc_id === ean_dsc_id) {
                        return {
                            ...q,
                            ean_answer: newAnswer.value ?? null,
                            ean_answer_text: newAnswer.text ?? newAnswer.value ?? null,
                            ean_saved: true,
                            ean_saved_at: new Date().toISOString()
                        };
                    }
                    return q;
                });

                return {
                    ...block,
                    questions: updatedBlockQuestions
                };
            });

            return {
                ...prev,
                evr_questions: updatedQuestions
            };
        });
    };

    const handleObservationChange = (evl_id, cmp_id, dsc_id, obsText) => {
        setEvaluationData(prev => {
            const updatedObservations = prev.evr_observations.map(obs => {
                if (
                    obs.eob_evl_id === evl_id &&
                    obs.eob_cmp_id === cmp_id &&
                    obs.eob_dsc_id === dsc_id
                ) {
                    return {
                        ...obs,
                        eob_observation: obsText
                    };
                }
                return obs;
            });

            return {
                ...prev,
                evr_observations: updatedObservations
            };
        });
    };

    const saveEvaluationAnswers = (finalize = false, newCurrentStep) => {
        if (isSavingEvaluation) {
            return;
        }

        setIsSavingEvaluation(true);
        setLoadingText(finalize
            ? "Enviando encuesta, espera un momento por favor..."
            : "Guardando respuestas, espera un momento por favor...")
        setShowLoading(true);
        let updatedData = { ...evaluationData };

        if (finalize === true) {
            updatedData.evr_finished = true;
        }

        const shouldRegisterEvaluationAcceptance = !finalize && Number(newCurrentStep) === 1;

        const proceedSave = () => {
            axios.post(`${apiUrl}/evaluation-relationships/save-evaluation-answers/`, updatedData)
                .then(function (response) {
                    setIsSavingEvaluation(false);
                    getEvaluationByUUID(false)
                    if (finalize) {
                        message.success("Evaluación finalizada");
                    } else {
                        if (newCurrentStep-1 != 0) {
                            message.success("Respuestas guardadas");
                        }
                    }

                })
                .catch(error => {
                    console.error('There was an error!', error);
                    setIsSavingEvaluation(false);
                    setShowLoading(false);
                });
        };

        if (shouldRegisterEvaluationAcceptance) {
            registerEvaluationTermsAcceptance()
                .catch((error) => {
                    // No bloquea el flujo de la encuesta; deja evidencia en consola para diagnóstico.
                    console.error('No se pudo registrar aceptación TyC por evaluación:', error);
                })
                .finally(() => {
                    proceedSave();
                });
            return;
        }

        proceedSave();
    };

    const showEvaluation = !showLoading && evaluationData;

    const totalSteps = (evaluationData?.evr_questions?.length || 0) + 2;
    const isLastStep = currentStep === totalSteps - 1;
    const progressPercent = totalSteps > 1
        ? (isLastStep ? 99 : Math.round((currentStep / (totalSteps - 1)) * 100))
        : 0;

    return <div className='public-evaluation-view'>
        {
            showLoading && <LoadingOverlay text={loadingText} showProgress={false} progress={null} />
        }
        <Layout style={{ width: '100%', height: '100vh', minHeight: 0, overflow: 'hidden' }}>
            <HeaderEvaluation orgLogo={orgLogo} />
            <Content
                className='public-evaluation-content'
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    padding: '24px 24px 48px',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    position: 'relative',
                }}
            >
                {showEvaluation && (
                    <div className='public-evaluation-shell'>
                        {
                            evaluationData && (
                                <div className='public-evaluation-info-bar'>
                                    <div className='public-evaluation-info-card'>
                                        <div className='public-evaluation-info-header'>
                                            <div className='public-evaluation-info-header-content'>
                                                <div className='public-evaluation-info-label'>Nombre del evaluado</div>
                                                <div className='public-evaluation-info-name'>
                                                    {evaluationData?.evr_evaluated_fullname || 'Sin información'}
                                                </div>
                                                <div className='public-evaluation-info-role-row'>
                                                    <span className='public-evaluation-info-label'>Cargo</span>
                                                    <span className='public-evaluation-info-role'>
                                                        {evaluationData?.evr_evaluated_job_title || 'Sin información'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className='public-evaluation-progress-badge'>
                                                {progressPercent}%
                                            </div>
                                        </div>
                                        <div className='public-evaluation-progress-row'>
                                            <div className='public-evaluation-progress-meta'>
                                                <span className='public-evaluation-info-label'>Avance</span>
                                                <span className='public-evaluation-progress-step'>
                                                    Paso {Math.min(currentStep + 1, totalSteps)} de {totalSteps}
                                                </span>
                                            </div>
                                            <Progress percent={progressPercent} showInfo={false} strokeColor="#1890ff" />
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                        {
                            evaluationData && (
                                <StepCompetencesEvaluation
                                    questions={evaluationData?.evr_questions || []}
                                    evaluation={evaluationData}
                                    dataEvaluationAnswerGroups={dataEvaluationAnswerGroups}
                                    onAnswerChange={handleAnswerChange}
                                    onObservationChange={handleObservationChange}
                                    saveEvaluationAnswers={saveEvaluationAnswers}
                                    currentStep={currentStep}
                                    onStepChange={setCurrentStep}
                                    isSavingEvaluation={isSavingEvaluation}
                                />
                            )
                        }
                    </div>
                )}

                {/* Footer de política de datos */}
                {showEvaluation && policyVersion && (
                    <div className='policy-footer-note'>
                        Al responder aceptas nuestra{' '}
                        <span
                            className='policy-footer-view-full'
                            onClick={() => setShowPolicyModal(true)}
                        >
                            Política de Tratamiento de Datos
                        </span>
                    </div>
                )}
            </Content>
            <FooterLanding />

            {/* Modal para ver la política completa desde el footer */}
            <Modal
                open={showPolicyModal}
                onCancel={() => setShowPolicyModal(false)}
                footer={null}
                title={`Política de Tratamiento de Datos v${policyVersion || ''}`}
                width={720}
                centered
                className='policy-modal'
            >
                <div
                    className='policy-modal-content'
                    dangerouslySetInnerHTML={{ __html: policyContent || '<p>Cargando…</p>' }}
                />
            </Modal>
        </Layout>
    </div>
}

export default EvaluationFormView;